// Copyright (c) Ping. All rights reserved.

import type { TaskFilter, TaskPriority, TaskSort, TaskState } from './types';

/**
 * UI copy for the Tasks module. Keys here are the source of truth for
 * `ping_tasks_*` localised strings; the synthesis pipeline regenerates
 * the iOS / Android catalogues from them.
 */
export const tasksStrings = {
  screenTitle: 'Tasks',
  searchPlaceholder: 'Search tasks',
  searchClearLabel: 'Clear search',
  filter: (kind: TaskFilter): string => {
    switch (kind) {
      case 'all':
        return 'All';
      case 'pending':
        return 'Pending';
      case 'completed':
        return 'Completed';
    }
  },
  state: (state: TaskState): string => {
    switch (state) {
      case 'backlog':
        return 'Backlog';
      case 'pending':
        return 'Pending';
      case 'inProgress':
        return 'In progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
    }
  },
  priority: (priority: TaskPriority): string => {
    switch (priority) {
      case 'low':
        return 'Low';
      case 'medium':
        return 'Medium';
      case 'high':
        return 'High';
      case 'urgent':
        return 'Urgent';
    }
  },
  sort: (sort: TaskSort): string => {
    switch (sort) {
      case 'mostRecent':
        return 'Most recent';
      case 'priority':
        return 'Priority';
      case 'dueDate':
        return 'Due date';
    }
  },
  emptyTitle: (filter: TaskFilter, query: string): string => {
    if (query.length > 0) {
      return 'No tasks match your search';
    }
    if (filter === 'all') {
      return 'No tasks yet';
    }
    return `No ${tasksStrings.filter(filter).toLowerCase()} tasks`;
  },
  emptyBody: 'Create a task to keep track of work across your team.',
  errorTitle: "Couldn't load tasks",
  errorRetry: 'Try again',
  newTaskTitle: 'New task',
  editTaskTitle: 'Edit task',
  newTaskCta: 'New task',
  saveCta: 'Save',
  cancelCta: 'Cancel',
  doneCta: 'Done',
  taskTitleLabel: 'Title',
  taskTitlePlaceholder: 'What needs to happen?',
  priorityLabel: 'Priority',
  dueLabel: 'Due',
  noDue: 'No due date',
  folderLabel: 'Folder',
  folderNone: 'No folder',
  selection: {
    enter: 'Select',
    exit: 'Done',
    bulkComplete: 'Mark complete',
    bulkDelete: 'Delete',
    bulkMove: 'Move to folder',
    clear: 'Clear selection',
    countLabel: (count: number): string => (count === 1 ? '1 selected' : `${count} selected`),
  },
  optionMenu: {
    rename: 'Rename',
    move: 'Move to folder',
    duplicate: 'Duplicate',
    delete: 'Delete',
  },
  dueRelative: (dueAt: number, now: number): string => {
    const diffMs = dueAt - now;
    const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
    if (diffDays === 0) {
      return 'Due today';
    }
    if (diffDays === 1) {
      return 'Due tomorrow';
    }
    if (diffDays === -1) {
      return 'Yesterday';
    }
    if (diffDays > 1 && diffDays < 7) {
      return `Due in ${diffDays} days`;
    }
    if (diffDays < -1 && diffDays > -7) {
      return `${Math.abs(diffDays)} days overdue`;
    }
    return new Date(dueAt).toLocaleDateString();
  },
  toggleAccessibility: (state: TaskState): string =>
    state === 'completed' ? 'Mark incomplete' : 'Mark complete',
} as const;

export type TasksStrings = typeof tasksStrings;
